import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "@/hooks/use-toast";
import {
  Handshake, Send, Loader2, CheckCircle, XCircle, Upload,
  CreditCard, Package, Eye, UserPlus, Clock, FileText, Star, Copy, Ban, DollarSign, ImageIcon, Coins, MessageCircle, QrCode, X
} from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import AvaliarForm from "@/components/AvaliarForm";
import IntermediacaoCountdown from "@/components/IntermediacaoCountdown";
import { useConfiguracoes } from "@/hooks/useConfiguracoes";
import logoImg from "@/assets/logo.webp";
import { RubinotCharacterValidator, type RubinotCharacterValidatorHandle } from "@/components/RubinotCharacterValidator";
import { CheckCircle2 } from "lucide-react";

const statusLabels: Record<string, string> = {
  aguardando_anunciante: "Aguardando Anunciante",
  recusado: "Recusado",
  aguardando_pagamento_intermedio: "Aguardando Pagamento Intermédio",
  aguardando_intermediador: "Aguardando Intermediador",
  em_andamento: "Em Andamento",
  aguardando_pagamento_item: "Aguardando Pagamento do Item",
  aguardando_conferencia_admin: "Aguardando Conferência Admin",
  aguardando_envio_item: "Aguardando Envio do Item",
  aguardando_confirmacao_recebimento: "Aguardando Confirmação de Recebimento",
  aguardando_pagamento_anunciante: "Aguardando Pagamento ao Anunciante",
  aguardando_confirmacao_pagamento: "Aguardando Confirmação de Pagamento",
  concluido: "Concluído",
  cancelado: "Cancelado",
};

const statusColors: Record<string, string> = {
  aguardando_anunciante: "bg-yellow-500/20 text-yellow-400",
  recusado: "bg-destructive/20 text-destructive",
  aguardando_pagamento_intermedio: "bg-orange-500/20 text-orange-400",
  aguardando_intermediador: "bg-blue-500/20 text-blue-400",
  em_andamento: "bg-cyan-500/20 text-cyan-400",
  aguardando_pagamento_item: "bg-orange-500/20 text-orange-400",
  aguardando_conferencia_admin: "bg-purple-500/20 text-purple-400",
  aguardando_envio_item: "bg-amber-500/20 text-amber-400",
  aguardando_confirmacao_recebimento: "bg-teal-500/20 text-teal-400",
  aguardando_pagamento_anunciante: "bg-indigo-500/20 text-indigo-400",
  aguardando_confirmacao_pagamento: "bg-pink-500/20 text-pink-400",
  concluido: "bg-green-500/20 text-green-400",
  cancelado: "bg-muted text-muted-foreground",
};

interface IntermediacaoTab {
  id: string;
  itemNome: string;
  itemImageUrl: string | null;
  otherParty: string;
}

interface IntermedioChatDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  intermediacaoId: string;
  sessionContato: string;
  sessionToken: string;
  isAdmin?: boolean;
  onStatusChange?: () => void;
  tabs?: IntermediacaoTab[];
  onSwitchTab?: (id: string) => void;
  unreadIds?: Set<string>;
}

const IntermedioChatDialog = ({
  open,
  onOpenChange,
  intermediacaoId,
  sessionContato,
  sessionToken,
  isAdmin = false,
  onStatusChange,
  tabs,
  onSwitchTab,
  unreadIds,
}: IntermedioChatDialogProps) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [msgInput, setMsgInput] = useState("");
  const [sending, setSending] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [justificativa, setJustificativa] = useState("");
  const [justificativaCancelamento, setJustificativaCancelamento] = useState("");
  const [chavePix, setChavePix] = useState("");
  const [nomeTitularPix, setNomeTitularPix] = useState("");
  const [personagemAnunciante, setPersonagemAnunciante] = useState("");
  const [personagemAnuncianteValidated, setPersonagemAnuncianteValidated] = useState(false);
  const [personagemAnuncianteLastValidated, setPersonagemAnuncianteLastValidated] = useState("");
  const [pendingAceitar, setPendingAceitar] = useState(false);
  const charValidatorRef = useRef<RubinotCharacterValidatorHandle>(null);
  const personagemAnuncianteInputRef = useRef<HTMLInputElement>(null);
  const [chavePixReembolso, setChavePixReembolso] = useState("");
  const [nomeTitularReembolso, setNomeTitularReembolso] = useState("");
  const [showRecusar, setShowRecusar] = useState(false);
  const [showCancelar, setShowCancelar] = useState(false);
  const [showReajuste, setShowReajuste] = useState(false);
  const [novoValor, setNovoValor] = useState("");
  const [novoValorCoins, setNovoValorCoins] = useState("");
  const [reajusteModalidade, setReajusteModalidade] = useState<"dinheiro" | "coins" | "ambos">("dinheiro");
  const [showAceitarReajuste, setShowAceitarReajuste] = useState(false);
  const [personagemReceptorCoins, setPersonagemReceptorCoins] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const msgInputRef = useRef<HTMLInputElement>(null);
  const reajusteFormRef = useRef<HTMLDivElement>(null);
  const novoValorInputRef = useRef<HTMLInputElement>(null);
  const [showAvaliacao, setShowAvaliacao] = useState(false);
  const prevMsgCountRef = useRef<number>(0);
  const [envioFile, setEnvioFile] = useState<File | null>(null);
  const [coinsFile, setCoinsFile] = useState<File | null>(null);
  const [itemImageUrl, setItemImageUrl] = useState<string | null>(null);
  const [personagemColetor, setPersonagemColetor] = useState<string | null>(null);
  const [lastActivityAt, setLastActivityAt] = useState<number>(Date.now());
  const [openedAt, setOpenedAt] = useState<number>(Date.now());
  const [showTimeExpiredDialog, setShowTimeExpiredDialog] = useState(false);
  const [qrPayment, setQrPayment] = useState<{ valor: number; qrCodeBase64?: string; qrCode?: string; url?: string } | null>(null);
  const qrDismissedRef = useRef<Set<string>>(new Set());
  const currentQrPaymentIdRef = useRef<string | null>(null);
  const [paymentConfirmedDialog, setPaymentConfirmedDialog] = useState<{ payerName: string; valor: number; tipo?: "intermedio" | "item" } | null>(null);
  const acknowledgedPaidRef = useRef<Set<string>>(new Set());
  const acknowledgedItemPaidRef = useRef<boolean>(false);
  const [showEnvioVideoDialog, setShowEnvioVideoDialog] = useState(false);
  const [envioVideoError, setEnvioVideoError] = useState<string | null>(null);
  const prevStatusRef = useRef<string | null>(null);
  const { data: configData } = useConfiguracoes();

  // AudioContext singleton — instantiate once and reuse
  const audioCtxRef = useRef<AudioContext | null>(null);

  const playNotificationSound = useCallback(() => {
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") ctx.resume();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = "sine";
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.setValueAtTime(1100, ctx.currentTime + 0.1);
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.3);
    } catch { /* ignore audio errors */ }
  }, []);

  const loadDetails = async () => {
    try {
      const body: any = { action: "detalhes", intermediacao_id: intermediacaoId };
      body.contato = sessionContato;
      body.token = sessionToken;
      const { data: res } = await supabase.functions.invoke("intermediacao", { body });
      if (res?.error) {
        toast({ title: res.error, variant: "destructive" });
        return;
      }
      // Play sound if new messages arrived
      const newMsgCount = res?.mensagens?.length || 0;
      if (prevMsgCountRef.current > 0 && newMsgCount > prevMsgCountRef.current) {
        playNotificationSound();
        setLastActivityAt(Date.now());
      }
      prevMsgCountRef.current = newMsgCount;
      setData(res);
      // Set lastActivityAt from latest message on first load
      if (prevMsgCountRef.current === newMsgCount || prevMsgCountRef.current === 0) {
        const msgs = res?.mensagens || [];
        if (msgs.length > 0) {
          const lastMsg = msgs[msgs.length - 1];
          const lastMsgTime = new Date(lastMsg.created_at).getTime();
          if (lastMsgTime > 0) setLastActivityAt(lastMsgTime);
        }
      }
      if (res?.item_image_url) setItemImageUrl(res.item_image_url);
      if (res?.personagem_coletor) setPersonagemColetor(res.personagem_coletor);
      if (res?.intermediacao?.chave_pix_anunciante) {
        setChavePix(res.intermediacao.chave_pix_anunciante);
      } else if (!chavePix && res?.intermediacao?.anunciante?.chave_pix && res?.intermediacao?.anunciante_id === res?.perfil_id) {
        setChavePix(res.intermediacao.anunciante.chave_pix);
      }
      if (res?.intermediacao?.nome_titular_pix_anunciante && !nomeTitularPix) {
        setNomeTitularPix(res.intermediacao.nome_titular_pix_anunciante);
      }
      if (res?.intermediacao?.personagem_anunciante && !personagemAnunciante) {
        setPersonagemAnunciante(res.intermediacao.personagem_anunciante);
        setPersonagemAnuncianteLastValidated(res.intermediacao.personagem_anunciante);
        setPersonagemAnuncianteValidated(true);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && intermediacaoId) {
      setLoading(true);
      acknowledgedPaidRef.current = new Set();
      acknowledgedItemPaidRef.current = false;
      prevStatusRef.current = null;
      setOpenedAt(Date.now());
      loadDetails();
    }
  }, [open, intermediacaoId]);

  // Realtime subscription for messages + status + payments
  useEffect(() => {
    if (!open || !intermediacaoId) return;

    const channel = supabase
      .channel(`intermediacao-msgs-${intermediacaoId}`)
      .on("postgres_changes", {
        event: "INSERT",
        schema: "public",
        table: "intermediacoes_mensagens",
        filter: `intermediacao_id=eq.${intermediacaoId}`,
      }, () => {
        loadDetails();
      })
      .subscribe();

    const statusChannel = supabase
      .channel(`intermediacao-status-${intermediacaoId}`)
      .on("postgres_changes", {
        event: "UPDATE",
        schema: "public",
        table: "intermediacoes",
        filter: `id=eq.${intermediacaoId}`,
      }, (payload: any) => {
        loadDetails();
        setLastActivityAt(Date.now());
        onStatusChange?.();
        // QR dialog only closes when the user's OWN payment is confirmed (handled in the auto-open effect)
        if (payload.new?.status === "concluido") {
          setShowAvaliacao(true);
        }
        // Close dialog if cancelled (keep open when multiple tabs so user can review history)
        if (payload.new?.status === "cancelado") {
          setTimeout(() => {
            if (!(tabs && tabs.length > 1)) {
              onOpenChange(false);
            }
            onStatusChange?.();
          }, 2000);
        }
      })
      .subscribe();

    // Listen for payment changes to auto-refresh (both INSERT and UPDATE)
    const paymentChannel = supabase
      .channel(`intermediacao-pag-${intermediacaoId}`)
      .on("postgres_changes", {
        event: "*",
        schema: "public",
        table: "intermediacoes_pagamentos",
        filter: `intermediacao_id=eq.${intermediacaoId}`,
      }, () => {
        loadDetails();
        onStatusChange?.();
      })
      .subscribe();

    // Polling fallback since RLS blocks realtime for non-admin users
    const pollInterval = setInterval(() => {
      if (document.hidden) return;
      loadDetails();
    }, 5000);

    return () => {
      clearInterval(pollInterval);
      supabase.removeChannel(channel);
      supabase.removeChannel(statusChannel);
      supabase.removeChannel(paymentChannel);
    };
  }, [open, intermediacaoId]);

  // Auto-scroll only on initial open, when user sends a new message, or when user is already near the bottom.
  // Prevents losing scroll position when reading older messages while autorefresh runs.
  const lastScrolledMsgCountRef = useRef<number>(0);
  useEffect(() => {
    const msgs = data?.mensagens || [];
    const count = msgs.length;
    if (count === 0) return;
    const endEl = messagesEndRef.current;
    if (!endEl) return;

    // Find scroll viewport (shadcn ScrollArea)
    const viewport = endEl.closest("[data-radix-scroll-area-viewport]") as HTMLElement | null;

    // First render with messages: jump to bottom (no smooth)
    if (lastScrolledMsgCountRef.current === 0) {
      endEl.scrollIntoView({ behavior: "auto" });
      lastScrolledMsgCountRef.current = count;
      return;
    }

    // No new messages → don't move scroll
    if (count <= lastScrolledMsgCountRef.current) return;

    // Only auto-scroll if user is near the bottom (within 80px)
    const nearBottom = viewport
      ? viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight < 80
      : true;

    if (nearBottom) {
      endEl.scrollIntoView({ behavior: "smooth" });
    }
    lastScrolledMsgCountRef.current = count;
  }, [data?.mensagens]);

  // Reset scroll tracker when dialog closes/opens
  useEffect(() => {
    if (!open) lastScrolledMsgCountRef.current = 0;
  }, [open]);

  // Auto-open QR dialog when status is aguardando_pagamento_intermedio and user has pending payment.
  // Also auto-close as soon as the user's OWN payment is marked as pago (independent of the other party).
  useEffect(() => {
    if (!data?.intermediacao || !data?.pagamentos) return;
    const inter = data.intermediacao;
    const pagamentos = data.pagamentos || [];
    const pid = data.perfil_id;

    // Detect any payment newly transitioned to "pago" and show confirmation dialog (once per payment)
    const getPayerName = (pagadorId: string): string => {
      if (inter.interessado_id === pagadorId) {
        return inter.interessado?.apelido || inter.interessado?.nome || "Interessado";
      }
      if (inter.anunciante_id === pagadorId) {
        return inter.anunciante?.apelido || inter.anunciante?.nome || "Anunciante";
      }
      return "Usuário";
    };

    const paidPagamentos = pagamentos.filter((p: any) => p.status === "pago");
    for (const p of paidPagamentos) {
      if (!acknowledgedPaidRef.current.has(p.id)) {
        // Skip on the very first load (no prior knowledge): prime as acknowledged silently
        if (!acknowledgedPaidRef.current.has("__primed__")) continue;
        acknowledgedPaidRef.current.add(p.id);
        // Skip item payments here — they have a dedicated confirmation dialog below
        // (status-transition block) to avoid showing two "OK" dialogs to the interessado.
        if (p.tipo === "item") continue;
        // Only show confirmation dialog to the actual payer
        if (p.pagador_id === pid) {
          setPaymentConfirmedDialog({ payerName: getPayerName(p.pagador_id), valor: Number(p.valor), tipo: "intermedio" });
        }
      }
    }
    // Prime acknowledged set on first run so we only react to NEW transitions
    if (!acknowledgedPaidRef.current.has("__primed__")) {
      acknowledgedPaidRef.current.add("__primed__");
      paidPagamentos.forEach((p: any) => acknowledgedPaidRef.current.add(p.id));
    }

    const myPending = pagamentos.find(
      (p: any) => p.status === "pendente" && p.pagador_id === pid && p.pix_qr_code_base64
    );
    const myPaid = pagamentos.find(
      (p: any) => p.status === "pago" && p.pagador_id === pid
    );

    // Close QR dialog (intermédio fee) when the specific payment shown becomes "pago",
    // OR when the user's own fee payment is confirmed (covers split and single-payer cases,
    // including when status auto-advances after both parts pay).
    if (qrPayment) {
      const shownPagId = currentQrPaymentIdRef.current;
      const shownPag = shownPagId ? pagamentos.find((p: any) => p.id === shownPagId) : null;
      const shownIsPaid = shownPag?.status === "pago";
      const isFeePhase = inter.status === "aguardando_pagamento_intermedio" || shownPag != null;
      if (isFeePhase && (shownIsPaid || (myPaid && !myPending))) {
        setQrPayment(null);
        currentQrPaymentIdRef.current = null;
        return;
      }
    }

    // Detect item payment confirmation (status moved past aguardando_pagamento_item).
    // Close item QR dialog and show payment confirmation to the interessado.
    const itemPaymentDoneStatuses = [
      "aguardando_conferencia_admin",
      "aguardando_envio_item",
      "aguardando_confirmacao_recebimento",
      "aguardando_pagamento_anunciante",
      "aguardando_confirmacao_pagamento",
      "concluido",
    ];
    if (
      prevStatusRef.current === "aguardando_pagamento_item" &&
      itemPaymentDoneStatuses.includes(inter.status) &&
      inter.interessado_id === pid &&
      !acknowledgedItemPaidRef.current
    ) {
      acknowledgedItemPaidRef.current = true;
      // Close QR dialog if open
      if (qrPayment) {
        setQrPayment(null);
        currentQrPaymentIdRef.current = null;
      }
      const interessadoNome = inter.interessado?.apelido || inter.interessado?.nome || "Você";
      setPaymentConfirmedDialog({
        payerName: interessadoNome,
        valor: Number(inter.valor_negociado || 0),
        tipo: "item",
      });
    }
    prevStatusRef.current = inter.status;

    if (qrPayment) return;

    // Auto-open QR for intermédio fee
    if (inter.status === "aguardando_pagamento_intermedio" && myPending) {
      if (qrDismissedRef.current.has(myPending.id)) return;
      setQrPayment({
        valor: Number(myPending.valor),
        qrCodeBase64: myPending.pix_qr_code_base64,
        qrCode: myPending.pix_qr_code || undefined,
        url: myPending.abacatepay_url || undefined,
      });
      currentQrPaymentIdRef.current = myPending.id;
      return;
    }

    // Auto-open QR for item payment (interessado) when admin releases payment
    if (
      inter.status === "aguardando_pagamento_item" &&
      inter.interessado_id === pid &&
      (inter.item_pix_qr_code_base64 || inter.item_pix_qr_code || inter.abacatepay_item_url)
    ) {
      const itemKey = `item-${inter.id}`;
      if (qrDismissedRef.current.has(itemKey)) return;
      setQrPayment({
        valor: Number(inter.valor_negociado),
        qrCodeBase64: inter.item_pix_qr_code_base64 || undefined,
        qrCode: inter.item_pix_qr_code || undefined,
        url: inter.abacatepay_item_url || undefined,
      });
      currentQrPaymentIdRef.current = itemKey;
    }
  }, [data?.intermediacao?.status, data?.pagamentos, data?.perfil_id]);

  // Auto-open shipping-instructions dialog when status becomes aguardando_envio_item
  // for the item sender; auto-close as soon as status moves on (video detected).
  useEffect(() => {
    const inter = data?.intermediacao;
    if (!inter || !data?.perfil_id) return;
    const pid = data.perfil_id;
    const tipoAnuncio = inter.anuncios?.tipo || "venda";
    const senderId = tipoAnuncio === "compra" ? inter.interessado_id : inter.anunciante_id;
    const userIsItemSender = senderId === pid;
    if (inter.status === "aguardando_envio_item" && userIsItemSender && !isAdmin) {
      if (!showEnvioVideoDialog) {
        setEnvioVideoError(null);
        setShowEnvioVideoDialog(true);
      }
    } else if (showEnvioVideoDialog && inter.status !== "aguardando_envio_item") {
      setShowEnvioVideoDialog(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.intermediacao?.status, data?.perfil_id, isAdmin]);

  const sendMessage = async () => {
    if (!msgInput.trim()) return;
    setSending(true);
    try {
      await supabase.functions.invoke("intermediacao", {
        body: {
          action: "enviar_mensagem",
          contato: sessionContato,
          token: sessionToken,
          intermediacao_id: intermediacaoId,
          conteudo: msgInput.trim(),
        },
      });
      setMsgInput("");
      setLastActivityAt(Date.now());
      // Refocus the input after sending
      setTimeout(() => msgInputRef.current?.focus(), 100);
    } catch (e) {
      toast({ title: "Erro ao enviar mensagem", variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  const handleAction = async (action: string, extraBody: any = {}) => {
    setActionLoading(action);
    try {
      const { data: res } = await supabase.functions.invoke("intermediacao", {
        body: {
          action,
          contato: sessionContato,
          token: sessionToken,
          intermediacao_id: intermediacaoId,
          ...extraBody,
        },
      });
      if (res?.error) {
        toast({ title: res.error, variant: "destructive" });
      } else {
        // Action-specific feedback (avoid misleading "payment confirmed" toasts
        // for actions that only generate a QR code, etc.)
        const actionToastTitles: Record<string, string> = {
          gerar_pagamento_item: "QR Code gerado! Realize o pagamento.",
          aceitar: "Proposta aceita!",
          recusar: "Proposta recusada.",
          propor_reajuste: "Proposta enviada.",
          cancelar_reajuste: "Proposta cancelada.",
          aceitar_reajuste: "Reajuste aceito!",
          recusar_reajuste: "Reajuste recusado.",
          confirmar_pagamento_item: "Pagamento confirmado!",
          confirmar_envio: "Envio confirmado!",
          confirmar_recebimento: "Recebimento confirmado!",
        };
        const title = actionToastTitles[action] || "Ação realizada com sucesso!";
        toast({ title });
        // Close dialog after cancel (keep open when multiple tabs so user can review history)
        if (action === "cancelar") {
          onStatusChange?.();
          if (!(tabs && tabs.length > 1)) {
            onOpenChange(false);
          } else {
            loadDetails();
          }
          return;
        }
        loadDetails();
        onStatusChange?.();
      }
    } catch (e) {
      toast({ title: "Erro ao processar ação", variant: "destructive" });
    } finally {
      setActionLoading(null);
      setShowRecusar(false);
      setShowCancelar(false);
      setShowReajuste(false);
      setShowAceitarReajuste(false);
    }
  };

  // Anunciante aceita: se houver reajuste pendente do interessado, aceita o reajuste e o intermédio em sequência
  const handleAceitarAnunciante = async (personagemNome: string) => {
    const reajustePendenteParaMim = inter?.reajuste_pendente && inter.reajuste_pendente.proposto_por !== perfilId;
    if (reajustePendenteParaMim) {
      const rMod = (inter?.reajuste_pendente?.modalidade || "dinheiro") as string;
      const rIncluiCoins = rMod === "coins" || rMod === "ambos";
      const tipoAnuncio = (inter?.anuncios?.tipo || "venda") as string;
      // No tipo venda, receptor de coins = interessado; em compra = anunciante
      const personagemReceptor = tipoAnuncio === "compra"
        ? personagemNome
        : (inter?.personagem_interessado || "").toString().trim();
      if (rIncluiCoins && !personagemReceptor) {
        toast({ title: "Não foi possível identificar o personagem receptor das coins. Peça ao interessado para informar.", variant: "destructive" });
        return;
      }
      setActionLoading("responder_anunciante");
      try {
        const { data: res } = await supabase.functions.invoke("intermediacao", {
          body: {
            action: "responder_reajuste",
            contato: sessionContato,
            token: sessionToken,
            intermediacao_id: intermediacaoId,
            aceitar: true,
            personagem_receptor_coins: rIncluiCoins ? personagemReceptor : undefined,
          },
        });
        if (res?.error) {
          toast({ title: res.error, variant: "destructive" });
          setActionLoading(null);
          return;
        }
      } catch {
        toast({ title: "Erro ao aceitar reajuste", variant: "destructive" });
        setActionLoading(null);
        return;
      }
      setActionLoading(null);
    }
    await handleAction("responder_anunciante", {
      aceitar: true,
      chave_pix: chavePix,
      nome_titular_pix: nomeTitularPix.trim(),
      personagem_anunciante: personagemNome,
    });
  };

  // Upload file for envio confirmation
  const handleConfirmarEnvioComAnexo = async () => {
    if (!envioFile) {
      toast({ title: "Anexe uma imagem ou vídeo como comprovante de envio", variant: "destructive" });
      return;
    }
    setActionLoading("confirmar_envio_item");
    try {
      const ext = envioFile.name.split(".").pop() || "png";
      const filePath = `envio-${intermediacaoId}-${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase.storage.from("comprovantes-intermediacao").upload(filePath, envioFile);
      if (uploadError) {
        toast({ title: "Erro no upload do arquivo", variant: "destructive" });
        setActionLoading(null);
        return;
      }
      await handleAction("confirmar_envio_item", { comprovante_envio_url: `ci://${filePath}` });
      setEnvioFile(null);
    } catch {
      toast({ title: "Erro ao enviar comprovante", variant: "destructive" });
      setActionLoading(null);
    }
  };

  // Upload file for coins transfer confirmation
  const handleConfirmarEnvioCoins = async () => {
    if (!coinsFile) {
      toast({ title: "Anexe uma imagem ou vídeo como comprovante de envio das coins", variant: "destructive" });
      return;
    }
    setActionLoading("confirmar_envio_coins");
    try {
      const ext = coinsFile.name.split(".").pop() || "png";
      const filePath = `coins-envio-${intermediacaoId}-${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase.storage.from("comprovantes-intermediacao").upload(filePath, coinsFile);
      if (uploadError) {
        toast({ title: "Erro no upload do arquivo", variant: "destructive" });
        setActionLoading(null);
        return;
      }
      await handleAction("confirmar_envio_coins", { comprovante_coins_url: `ci://${filePath}` });
      setCoinsFile(null);
    } catch {
      toast({ title: "Erro ao enviar comprovante", variant: "destructive" });
      setActionLoading(null);
    }
  };

  const inter = data?.intermediacao;
  const mensagens = data?.mensagens || [];
  const pagamentos = data?.pagamentos || [];
  const perfilId = data?.perfil_id;

  const isInteressado = inter?.interessado_id === perfilId;
  const isAnunciante = inter?.anunciante_id === perfilId;
  const status = inter?.status || "";
  const modalidade = inter?.modalidade_pagamento || "dinheiro";
  const tipoAnuncio = inter?.anuncios?.tipo || "venda";
  // For "compra" type, the item sender/receiver roles are inverted
  const isItemSender = tipoAnuncio === "compra" ? isInteressado : isAnunciante;
  const isItemReceiver = tipoAnuncio === "compra" ? isAnunciante : isInteressado;
  const isCoinSender = tipoAnuncio === "compra" ? isAnunciante : isInteressado;

  // Cancel is now allowed at any time (except terminal)
  const terminalStatuses = ["concluido", "cancelado", "recusado"];
  const isTerminal = terminalStatuses.includes(status);

  // Check if any payment has been made (for refund flow)
  const algumPagamentoPago = pagamentos.some((p: any) => p.status === "pago");

  // After payment is made, only admin can cancel
  const canCancel = !isTerminal && (isAdmin || !algumPagamentoPago);

  // Price renegotiation
  const reajuste = inter?.reajuste_pendente;
  const reajustePropostoPorMim = reajuste?.proposto_por === perfilId;
  const participantePodeResponderReajuste = !isAdmin && (isInteressado || isAnunciante);
  const reajusteParaMimResponder = reajuste && participantePodeResponderReajuste && !reajustePropostoPorMim;
  const postPaymentStatuses = [
    "aguardando_conferencia_admin", "aguardando_envio_item",
    "aguardando_confirmacao_recebimento", "aguardando_pagamento_anunciante",
    "aguardando_confirmacao_pagamento",
  ];
  const isPostPayment = postPaymentStatuses.includes(status);
  // Hide propose-reajuste button once any intermédio payment QR has been generated/paid
  const algumPagamentoGerado = pagamentos.length > 0;
  // Pode propor enquanto não estiver em pagamento já gerado, e desde que não seja proposta minha pendente
  const canProposeReajuste = !isTerminal && !isPostPayment && (isInteressado || isAnunciante) && !isAdmin && !reajustePropostoPorMim && !algumPagamentoGerado;

  const formatDate = (d: string) => {
    try { return format(new Date(d), "dd/MM HH:mm", { locale: ptBR }); }
    catch { return ""; }
  };

  // Filter payment link messages to only show user's own link
  const filterPaymentLinksForUser = (content: string, senderId: string) => {
    if (!content.includes("💳 Links de pagamento:")) return content;
    
    const lines = content.split("\n");
    const filteredLines: string[] = [];
    let inPaymentSection = false;
    
    for (const line of lines) {
      if (line.includes("💳 Links de pagamento:")) {
        inPaymentSection = true;
        filteredLines.push(line);
        continue;
      }
      if (inPaymentSection && line.startsWith("R$ ") && line.includes("→")) {
        const userPayment = pagamentos.find((p: any) => 
          p.pagador_id === perfilId && p.abacatepay_url && line.includes(p.abacatepay_url)
        );
        if (userPayment || isAdmin) {
          filteredLines.push(line);
        } else {
          filteredLines.push("R$ " + line.split("→")[0].trim().replace("R$ ", "") + " → Aguardando pagamento da outra parte...");
        }
      } else {
        inPaymentSection = false;
        filteredLines.push(line);
      }
    }
    
    return filteredLines.join("\n");
  };

  // Build item image URL from data
  const resolvedItemImageUrl = itemImageUrl || (() => {
    const itemNome = inter?.anuncios?.item_nome || "";
    const formatted = itemNome.replace(/ /g, "_");
    return formatted
      ? `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/proxy-item-image?name=${encodeURIComponent(formatted)}`
      : null;
  })();

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent hideCloseButton className="max-w-2xl max-h-[85dvh] flex flex-col overflow-hidden p-0 gap-0" onPointerDownOutside={(e) => { if (showAvaliacao) e.preventDefault(); }} onEscapeKeyDown={(e) => { if (showAvaliacao) e.preventDefault(); }}>
        {/* Tabs at the very top - full width, with right padding for close button */}
        {tabs && tabs.length > 1 && (
          <div className="flex gap-0 overflow-x-auto border-b border-border/50 bg-secondary/50 shrink-0 pr-10">
            {tabs.map((tab) => {
              const isActive = tab.id === intermediacaoId;
              return (
                <button
                  key={tab.id}
                  onClick={() => onSwitchTab?.(tab.id)}
                  className={`relative flex items-center gap-1.5 px-3 py-2 transition-colors text-[10px] whitespace-nowrap shrink-0 border-b-2 ${
                    isActive
                      ? "border-primary bg-background text-primary"
                      : "border-transparent hover:bg-secondary text-muted-foreground"
                  }`}
                  title={`${tab.itemNome} — ${tab.otherParty}`}
                >
                  {!isActive && unreadIds?.has(tab.id) && (
                    <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-destructive rounded-full" />
                  )}
                  {tab.itemImageUrl && (
                    <img src={tab.itemImageUrl} alt="" className="w-5 h-5 object-contain" />
                  )}
                  <span className="max-w-[100px] truncate font-pixel text-[8px]">{tab.itemNome}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Countdown timer with close button */}
        {status ? (
          <div className="shrink-0 relative">
            <IntermediacaoCountdown
              status={status}
              lastActivityAt={lastActivityAt}
              openedAt={openedAt}
              onTimeExpired={async () => {
                try {
                  await supabase.functions.invoke("intermediacao", {
                    body: {
                      action: "cancelar",
                      contato: sessionContato,
                      token: sessionToken,
                      intermediacao_id: intermediacaoId,
                      justificativa: "Tempo de intermediação esgotado (15 minutos sem atividade)",
                      por_inatividade: true,
                    },
                  });
                } catch {
                  // ignore — still inform the user below
                } finally {
                  onStatusChange?.();
                  setShowTimeExpiredDialog(true);
                }
              }}
            />
            <DialogClose className="absolute right-3 top-1/2 -translate-y-1/2 rounded-sm opacity-70 hover:opacity-100 transition-opacity focus:outline-none focus:ring-2 focus:ring-ring">
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </DialogClose>
          </div>
        ) : (
          <DialogClose className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100 transition-opacity focus:outline-none focus:ring-2 focus:ring-ring z-10">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogClose>
        )}

        {/* Compact header: title + value + actions */}
        <div className="px-4 pt-3 pb-3 shrink-0">
          <div className="flex items-start justify-between gap-2 pr-2">
            <div className="flex items-center gap-2 min-w-0 flex-wrap">
              <Handshake className="w-4 h-4 text-primary shrink-0" />
              <span className="font-pixel text-xs whitespace-nowrap">Intermediação</span>
              {inter && (
                <span className="text-[10px] text-muted-foreground">
                  — R$ {Number(inter?.valor_intermedio || 0).toFixed(2).replace(".", ",")} ({inter?.pagador_intermedio})
                </span>
              )}
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              {/* WhatsApp support button */}
              {configData?.whatsapp_numero && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-6 text-[9px] font-pixel gap-1 shrink-0 px-2 text-green-500 border-green-500/30 hover:bg-green-500/10"
                  onClick={() => {
                    const itemNome = inter?.anuncios?.item_nome || "Item";
                    const interessadoNome = inter?.interessado?.apelido || inter?.interessado?.nome || "Interessado";
                    const anuncianteNome = inter?.anunciante?.apelido || inter?.anunciante?.nome || "Anunciante";
                    const msg = encodeURIComponent(`Intermédio ${itemNome} - ${interessadoNome} e ${anuncianteNome}`);
                    window.open(`https://wa.me/${configData.whatsapp_numero}?text=${msg}`, "_blank");
                  }}
                  title="Falar com suporte via WhatsApp"
                >
                  <MessageCircle className="w-3 h-3" />
                  Suporte
                </Button>
              )}
            {/* Cancel button — admin (intermediador) can always cancel; others hidden after payment */}
            {!loading && data?.intermediacao && canCancel && (
              <AlertDialog open={showCancelar} onOpenChange={setShowCancelar}>
                <AlertDialogTrigger asChild>
                  <Button
                    size="sm"
                    variant="destructive"
                    className="h-6 text-[9px] font-pixel gap-1 shrink-0 px-2"
                    disabled={!!actionLoading}
                    title="Cancelar intermediação"
                  >
                    <Ban className="w-3 h-3" />
                    Cancelar
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle className="font-pixel text-sm">Cancelar Intermediação</AlertDialogTitle>
                    <AlertDialogDescription>
                      {algumPagamentoPago
                        ? (isAdmin
                            ? "Pagamento(s) já efetuado(s). O reembolso será processado para os pagadores usando a chave PIX cadastrada de cada um."
                            : "Pagamento(s) já efetuado(s). O reembolso será processado pelo administrador. Informe o motivo e a chave PIX para recebimento.")
                        : "Informe o motivo do cancelamento. Todos os envolvidos serão notificados."
                      }
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <div className="py-2 space-y-3">
                    <div>
                      <Label className="text-xs">Justificativa do cancelamento *</Label>
                      <Textarea
                        value={justificativaCancelamento}
                        onChange={(e) => setJustificativaCancelamento(e.target.value)}
                        placeholder="Informe o motivo do cancelamento..."
                        rows={3}
                        className="mt-1"
                      />
                    </div>
                    {algumPagamentoPago && !isAdmin && (
                      <>
                        <div>
                          <Label className="text-xs">Chave PIX para reembolso *</Label>
                          <Input
                            value={chavePixReembolso}
                            onChange={(e) => setChavePixReembolso(e.target.value)}
                            placeholder="CPF, e-mail, telefone ou chave aleatória"
                            className="mt-1"
                          />
                          <p className="text-[9px] text-muted-foreground mt-1">
                            O valor pago será reembolsado após análise do administrador.
                          </p>
                        </div>
                        <div>
                          <Label className="text-xs">Nome do titular da chave PIX *</Label>
                          <Input
                            value={nomeTitularReembolso}
                            onChange={(e) => setNomeTitularReembolso(e.target.value)}
                            placeholder="Nome completo do titular"
                            maxLength={120}
                            className="mt-1"
                          />
                        </div>
                      </>
                    )}
                  </div>
                  <AlertDialogFooter>
                    <AlertDialogCancel onClick={() => { setJustificativaCancelamento(""); setChavePixReembolso(""); setNomeTitularReembolso(""); }}>Voltar</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => handleAction("cancelar", {
                        justificativa: justificativaCancelamento,
                        chave_pix_reembolso: isAdmin ? undefined : (chavePixReembolso || undefined),
                        nome_titular_pix_reembolso: isAdmin ? undefined : (nomeTitularReembolso || undefined),
                      })}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      disabled={!justificativaCancelamento.trim() || (algumPagamentoPago && !isAdmin && (!chavePixReembolso.trim() || !nomeTitularReembolso.trim()))}
                    >
                      {actionLoading === "cancelar" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmar Cancelamento"}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            </div>
          </div>
        </div>

        {/* Main content */}
        {loading ? (
          <div className="flex items-center justify-center py-12 px-6">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : !inter ? (
          <p className="text-center text-muted-foreground py-8 px-6">Intermediação não encontrada</p>
        ) : (
          <div
            className="flex-1 min-h-0 h-0 overflow-y-scroll [scrollbar-width:thin] [scrollbar-color:hsl(var(--primary))_hsl(var(--secondary))] [&::-webkit-scrollbar]:w-2.5 [&::-webkit-scrollbar-track]:bg-secondary/40 [&::-webkit-scrollbar-thumb]:bg-primary/60 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-primary"
          >
            <div className="flex flex-col gap-3 px-4 pb-4">
              {/* Status + Item + Participants compact header */}
              <div className="space-y-2 p-2 rounded-lg bg-secondary/30 border border-border/50 shrink-0 overflow-x-hidden">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <Badge className={`font-pixel text-[7px] ${statusColors[status] || "bg-muted text-muted-foreground"}`}>
                    {statusLabels[status] || status}
                  </Badge>
                  <span className="text-[10px] text-muted-foreground">{formatDate(inter.created_at)}</span>
                </div>

                {/* Item image + name + price + participants all in one row */}
                <div className="flex items-center gap-3 flex-wrap">
                  {resolvedItemImageUrl && (
                    <img
                      src={resolvedItemImageUrl}
                      alt={inter.anuncios?.item_nome || ""}
                      className="w-10 h-10 object-contain rounded border border-border/30 bg-background/50 shrink-0"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                    />
                  )}
                  <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
                    <div className="flex flex-col min-w-0">
                      <span className="text-primary font-medium text-xs truncate max-w-[180px]">{inter.anuncios?.item_nome}</span>
                      {inter.anuncios?.servidor && (
                        <span className="text-[9px] text-muted-foreground truncate max-w-[180px]">{inter.anuncios.servidor}</span>
                      )}
                    </div>
                    <span className="text-[10px] text-muted-foreground">
                      {modalidade === "dinheiro" && `R$ ${Number(inter.valor_negociado).toFixed(2).replace(".", ",")}`}
                      {modalidade === "coins" && `${Number(inter.valor_negociado_coins || 0).toLocaleString("pt-BR")} RC`}
                      {modalidade === "ambos" && `R$ ${Number(inter.valor_negociado).toFixed(2).replace(".", ",")} + ${Number(inter.valor_negociado_coins || 0).toLocaleString("pt-BR")} RC`}
                    </span>
                    <span className="text-[10px] text-muted-foreground">|</span>
                    {/* Interessado inline */}
                    <div className="flex items-center gap-1 shrink-0">
                      {inter.interessado?.avatar_url ? (
                        <img src={inter.interessado.avatar_url} alt="" className="w-4 h-4 rounded-full object-cover" />
                      ) : (
                        <div className="w-4 h-4 rounded-full bg-primary/20 flex items-center justify-center text-[7px] font-bold text-primary">
                          {(inter.interessado?.apelido || inter.interessado?.nome || "I")[0].toUpperCase()}
                        </div>
                      )}
                      <span className="text-[10px]">
                        {inter.interessado?.apelido || inter.interessado?.nome || "Interessado"}
                        {isInteressado && <span className="text-muted-foreground"> (eu)</span>}
                      </span>
                    </div>
                    <span className="text-[10px] text-muted-foreground">↔</span>
                    {/* Anunciante inline */}
                    <div className="flex items-center gap-1 shrink-0">
                      {inter.anunciante?.avatar_url ? (
                        <img src={inter.anunciante.avatar_url} alt="" className="w-4 h-4 rounded-full object-cover" />
                      ) : (
                        <div className="w-4 h-4 rounded-full bg-primary/20 flex items-center justify-center text-[7px] font-bold text-primary">
                          {(inter.anunciante?.apelido || inter.anunciante?.nome || "A")[0].toUpperCase()}
                        </div>
                      )}
                      <span className="text-[10px]">
                        {inter.anunciante?.apelido || inter.anunciante?.nome || "Anunciante"}
                        {isAnunciante && <span className="text-muted-foreground"> (eu)</span>}
                      </span>
                    </div>
                    {inter.intermediador && (
                      <>
                        <span className="text-[10px] text-muted-foreground">•</span>
                        <div className="flex items-center gap-1 shrink-0">
                          {isAdmin && inter.intermediador?.avatar_url ? (
                            <img src={inter.intermediador.avatar_url} alt="" className="w-4 h-4 rounded-full object-cover" />
                          ) : (
                            <div className="w-4 h-4 rounded-full bg-primary/20 flex items-center justify-center text-[7px] font-bold text-primary">
                              🛡️
                            </div>
                          )}
                          <span className="text-[10px]">
                            {isAdmin
                              ? (inter.intermediador.apelido || inter.intermediador.nome)
                              : "Intermediador online"}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {isAdmin && inter.chave_pix_anunciante && (
                  <div className="flex flex-col gap-0.5 text-xs">
                    <div className="flex items-center gap-1">
                      <span className="text-muted-foreground">PIX Anunciante:</span>{" "}
                      <span className="font-medium text-primary select-all break-all">{inter.chave_pix_anunciante}</span>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(inter.chave_pix_anunciante);
                          toast({ title: "Chave PIX copiada!" });
                        }}
                        className="ml-1 p-0.5 rounded hover:bg-secondary/50 transition-colors"
                        title="Copiar chave PIX"
                      >
                        <Copy className="w-3 h-3 text-muted-foreground hover:text-primary" />
                      </button>
                    </div>
                    {inter.nome_titular_pix_anunciante && (
                      <div className="flex items-center gap-1">
                        <span className="text-muted-foreground">Titular:</span>
                        <span className="font-medium text-foreground select-all">{inter.nome_titular_pix_anunciante}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Payment links for intermedio fee */}
                {status === "aguardando_pagamento_intermedio" && pagamentos.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {/* User's pending payment */}
                    {pagamentos.filter((p: any) => p.status === "pendente" && p.pagador_id === perfilId).map((p: any) => (
                      <div key={p.id} className="flex items-center gap-2 text-xs">
                        <Clock className="w-3 h-3 text-orange-400" />
                        <span>Seu pagamento:</span>
                        <Button
                          size="sm"
                          variant="default"
                          className="h-6 text-[9px] font-pixel gap-1 px-2"
                          onClick={() => {
                            if (p.pix_qr_code_base64) {
                              qrDismissedRef.current.delete(p.id);
                              currentQrPaymentIdRef.current = p.id;
                              setQrPayment({
                                valor: Number(p.valor),
                                qrCodeBase64: p.pix_qr_code_base64,
                                qrCode: p.pix_qr_code || undefined,
                                url: p.abacatepay_url || undefined,
                              });
                            } else if (p.abacatepay_url) {
                              window.open(p.abacatepay_url, "_blank");
                            }
                          }}
                        >
                          <QrCode className="w-3 h-3" />
                          Pagar R$ {Number(p.valor).toFixed(2).replace(".", ",")}
                        </Button>
                      </div>
                    ))}
                    {/* User's already paid */}
                    {pagamentos.filter((p: any) => p.status === "pago" && p.pagador_id === perfilId).map((p: any) => (
                      <div key={p.id} className="flex items-center gap-2 text-xs text-green-400">
                        <CheckCircle className="w-3 h-3" />
                        <span>Seu pagamento confirmado — R$ {Number(p.valor).toFixed(2).replace(".", ",")}</span>
                      </div>
                    ))}
                    {/* Other party pending */}
                    {pagamentos.filter((p: any) => p.status === "pendente" && p.pagador_id !== perfilId).length > 0 && !isAdmin && (
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Clock className="w-3 h-3" />
                        <span>Aguardando pagamento da outra parte...</span>
                      </div>
                    )}
                    {/* Other party already paid */}
                    {pagamentos.filter((p: any) => p.status === "pago" && p.pagador_id !== perfilId).length > 0 && !isAdmin && (
                      <div className="flex items-center gap-2 text-xs text-green-400">
                        <CheckCircle className="w-3 h-3" />
                        <span>A outra parte já efetuou o pagamento</span>
                      </div>
                    )}
                    {/* Admin view */}
                    {isAdmin && pagamentos.filter((p: any) => p.status !== "pago" || p.pagador_id === perfilId).length === 0 && pagamentos.map((p: any) => (
                      <div key={`admin-${p.id}`} className="flex items-center gap-2 text-xs">
                        {p.status === "pago" ? (
                          <CheckCircle className="w-3 h-3 text-green-400" />
                        ) : (
                          <Clock className="w-3 h-3 text-orange-400" />
                        )}
                        <span className="text-muted-foreground">Pagador: {p.pagador_id === inter.interessado_id ? "Interessado" : "Anunciante"}</span>
                        {p.abacatepay_url && (
                          <a href={p.abacatepay_url} target="_blank" rel="noopener noreferrer" className="text-primary underline hover:text-primary/80">
                            R$ {Number(p.valor).toFixed(2).replace(".", ",")}
                          </a>
                        )}
                        <span className={p.status === "pago" ? "text-green-400" : "text-orange-400"}>{p.status}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Coin transfer info banner - only show after intermediation fee is paid */}
                {(modalidade === "coins" || modalidade === "ambos") && !isTerminal && !["aguardando_anunciante", "aguardando_pagamento_intermedio"].includes(status) && (
                  <div className="mt-2 p-2 rounded-lg border border-primary/30 bg-primary/5">
                    <p className="text-xs font-medium flex items-center gap-1 text-primary">
                      <Coins className="w-3 h-3" />
                      {inter.coins_transferido
                        ? `✅ ${Number(inter.valor_negociado_coins || 0).toLocaleString("pt-BR")} RC confirmadas`
                        : `🪙 Transferir ${Number(inter.valor_negociado_coins || 0).toLocaleString("pt-BR")} RC para ${personagemColetor || "personagem coletor"}`
                      }
                    </p>
                    {!inter.coins_transferido && (
                      <p className="text-[9px] text-muted-foreground mt-1">
                        O administrador confirmará o recebimento das coins.
                      </p>
                    )}
                  </div>
                )}

              </div>

              {/* Messages */}
              <div className="min-h-[200px] max-h-[50dvh] overflow-y-auto space-y-2 p-2 rounded-lg bg-card/50 border border-border/30">
                {mensagens.map((msg: any) => {
                  const isSystem = msg.tipo === "sistema";
                  const isMine = msg.remetente_id === perfilId;
                  const displayContent = isSystem ? filterPaymentLinksForUser(msg.conteudo, msg.remetente_id) : msg.conteudo;
                  return (
                    <div key={msg.id} className={`flex ${isSystem ? "justify-center" : isMine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] rounded-lg px-3 py-1.5 text-xs ${
                        isSystem
                          ? "bg-muted/50 text-muted-foreground italic text-center"
                          : isMine
                          ? "bg-primary/20 text-foreground"
                          : "bg-secondary text-foreground"
                      }`}>
                        {!isSystem && (
                          <p className="text-[9px] font-bold text-primary/70 mb-0.5">
                            {msg.remetente?.apelido || msg.remetente?.nome || "Usuário"}
                          </p>
                        )}
                        <p className="whitespace-pre-wrap break-words">{displayContent.split(/(https?:\/\/[^\s]+)/g).map((part: string, i: number) =>
                          /^https?:\/\//.test(part) ? (
                            <span key={i} className="inline-flex items-center gap-1 bg-primary/10 rounded px-1.5 py-0.5 my-0.5">
                              <a href={part} target="_blank" rel="noopener noreferrer" className="text-primary underline hover:text-primary/80 text-[10px]">link de pagamento</a>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigator.clipboard.writeText(part);
                                  toast({ title: "Link copiado!" });
                                }}
                                className="inline-flex p-0.5 rounded hover:bg-secondary/50 transition-colors shrink-0"
                                title="Copiar link"
                              >
                                <Copy className="w-3 h-3 text-muted-foreground hover:text-primary" />
                              </button>
                            </span>
                          ) : part
                        )}</p>
                        {msg.arquivo_url && (() => {
                          const url: string = msg.arquivo_url;
                          const isPrivateRef = url.startsWith("wa://");
                          const isVideo = msg.tipo === "video" || /\.(mp4|webm|mov|m4v|3gp)(\?|$)/i.test(url);
                          const isImage = msg.tipo === "imagem" || /\.(png|jpe?g|gif|webp)(\?|$)/i.test(url);

                          const openSecure = async (e: React.MouseEvent) => {
                            e.preventDefault();
                            const { data, error } = await supabase.functions.invoke("intermediacao-media", {
                              body: { contato: sessionContato, token: sessionToken, mensagem_id: msg.id },
                            });
                            if (error || !data?.success || !data?.url) {
                              toast({ title: "Não foi possível carregar o anexo", variant: "destructive" });
                              return;
                            }
                            window.open(data.url, "_blank", "noopener,noreferrer");
                          };

                          if (isVideo) {
                            return (
                              <a
                                href={isPrivateRef ? "#" : url}
                                onClick={isPrivateRef ? openSecure : undefined}
                                target={isPrivateRef ? undefined : "_blank"}
                                rel="noopener noreferrer"
                                className="text-primary underline text-[10px] flex items-center gap-1 mt-1"
                              >
                                <FileText className="w-3 h-3" /> Reproduzir vídeo
                              </a>
                            );
                          }
                          if (isImage) {
                            if (isPrivateRef) {
                              return (
                                <a
                                  href="#"
                                  onClick={openSecure}
                                  className="text-primary underline text-[10px] flex items-center gap-1 mt-1"
                                >
                                  <FileText className="w-3 h-3" /> Ver imagem
                                </a>
                              );
                            }
                            return (
                              <a href={url} target="_blank" rel="noopener noreferrer" className="block mt-1">
                                <img src={url} alt="Anexo" className="max-w-full rounded border border-border/30" style={{ maxHeight: 220 }} />
                              </a>
                            );
                          }
                          return (
                            <a
                              href={isPrivateRef ? "#" : url}
                              onClick={isPrivateRef ? openSecure : undefined}
                              target={isPrivateRef ? undefined : "_blank"}
                              rel="noopener noreferrer"
                              className="text-primary underline text-[10px] flex items-center gap-1 mt-1"
                            >
                              <FileText className="w-3 h-3" /> Arquivo anexo
                            </a>
                          );
                        })()}
                        <span className="text-[8px] text-muted-foreground block text-right mt-0.5">{formatDate(msg.created_at)}</span>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Contextual action buttons */}
              <div className="space-y-2 shrink-0 overflow-x-hidden">
                {/* Proposal summary for anunciante */}
                {status === "aguardando_anunciante" && isAnunciante && (
                  <div className="p-3 rounded-lg border border-primary/30 bg-primary/5 space-y-1.5">
                    <p className="text-xs font-medium text-primary flex items-center gap-1">
                      <Handshake className="w-3.5 h-3.5" /> Proposta recebida
                    </p>
                    <div className="text-xs space-y-1">
                      {reajuste ? (() => {
                        const rMod = (reajuste.modalidade || "dinheiro") as string;
                        const rDin = rMod === "dinheiro" || rMod === "ambos";
                        const rCoins = rMod === "coins" || rMod === "ambos";
                        const origDin = modalidade === "dinheiro" || modalidade === "ambos";
                        const origCoins = modalidade === "coins" || modalidade === "ambos";
                        return (
                          <>
                            {rDin && (
                              <>
                                {origDin && (
                                  <p className="text-foreground">
                                    <span className="text-muted-foreground">Valor original:</span>{" "}
                                    <span className="line-through text-muted-foreground">R$ {Number(inter.valor_negociado).toFixed(2).replace(".", ",")}</span>
                                  </p>
                                )}
                                <p className="text-foreground">
                                  <span className="text-muted-foreground">Novo valor proposto:</span>{" "}
                                  <span className="font-bold text-amber-400">R$ {Number(reajuste.valor || 0).toFixed(2).replace(".", ",")}</span>
                                </p>
                              </>
                            )}
                            {rCoins && (
                              <>
                                {origCoins && (
                                  <p className="text-foreground">
                                    <span className="text-muted-foreground">Coins originais:</span>{" "}
                                    <span className="line-through text-muted-foreground">{Number(inter.valor_negociado_coins || 0).toLocaleString("pt-BR")} RC</span>
                                  </p>
                                )}
                                <p className="text-foreground">
                                  <span className="text-muted-foreground">Novas coins propostas:</span>{" "}
                                  <span className="font-bold text-amber-400">{Number(reajuste.valor_coins || 0).toLocaleString("pt-BR")} RC</span>
                                </p>
                              </>
                            )}
                            <p className="text-foreground">
                              <span className="text-muted-foreground">Nova modalidade:</span>{" "}
                              <span className="font-medium">
                                {rMod === "dinheiro" ? "Dinheiro (R$)" : rMod === "coins" ? "Coins (RC)" : "Dinheiro + Coins"}
                              </span>
                            </p>
                          </>
                        );
                      })() : (
                        <>
                          {(modalidade === "dinheiro" || modalidade === "ambos") && (
                            <p className="text-foreground">
                              <span className="text-muted-foreground">Valor:</span>{" "}
                              <span className="font-medium">R$ {Number(inter.valor_negociado).toFixed(2).replace(".", ",")}</span>
                            </p>
                          )}
                          {(modalidade === "coins" || modalidade === "ambos") && (
                            <p className="text-foreground">
                              <span className="text-muted-foreground">Coins:</span>{" "}
                              <span className="font-medium">{Number(inter.valor_negociado_coins || 0).toLocaleString("pt-BR")} RC</span>
                            </p>
                          )}
                        </>
                      )}
                      {!reajuste && (
                        <p className="text-foreground">
                          <span className="text-muted-foreground">Modalidade:</span>{" "}
                          <span className="font-medium">
                            {modalidade === "dinheiro" ? "Dinheiro (R$)" : modalidade === "coins" ? "Coins (RC)" : "Dinheiro + Coins"}
                          </span>
                        </p>
                      )}
                      <p className="text-foreground">
                        <span className="text-muted-foreground">Taxa do intermédio:</span>{" "}
                        <span className="font-medium">
                          {inter.pagador_intermedio === "interessado"
                            ? "Paga pelo interessado"
                            : inter.pagador_intermedio === "anunciante"
                            ? "Paga por você"
                            : "Dividida entre ambos"}
                        </span>
                      </p>
                    </div>
                  </div>
                )}

                {/* Inline reajuste form (rendered above action buttons) */}
                {status === "aguardando_anunciante" && isAnunciante && showReajuste && (() => {
                  const incluiDin = reajusteModalidade === "dinheiro" || reajusteModalidade === "ambos";
                  const incluiC = reajusteModalidade === "coins" || reajusteModalidade === "ambos";
                  const valNum = parseFloat(novoValor || "0");
                  const coinsNum = parseInt(novoValorCoins || "0", 10);
                  const valido =
                    (!incluiDin || (valNum > 0)) &&
                    (!incluiC || (coinsNum > 0));
                  return (
                    <div className="space-y-2 p-3 rounded-lg border border-amber-500/30 bg-amber-500/10">
                      <Label className="text-xs">Modalidade da proposta</Label>
                      <div className="flex gap-2 flex-wrap">
                        {(["dinheiro", "coins", "ambos"] as const).map((m) => (
                          <Button
                            key={m}
                            size="sm"
                            variant={reajusteModalidade === m ? "default" : "outline"}
                            className="h-7 text-[10px] capitalize"
                            onClick={() => setReajusteModalidade(m)}
                          >
                            {m === "dinheiro" ? "Dinheiro" : m === "coins" ? "Coins" : "Dinheiro + Coins"}
                          </Button>
                        ))}
                      </div>
                      {incluiDin && (
                        <div>
                          <Label className="text-xs">Valor em dinheiro (R$)</Label>
                          <Input type="number" step="0.01" min="0.01" value={novoValor} onChange={(e) => setNovoValor(e.target.value)} className="mt-1" />
                        </div>
                      )}
                      {incluiC && (
                        <div>
                          <Label className="text-xs">Valor em coins (RC)</Label>
                          <Input type="number" step="1" min="1" value={novoValorCoins} onChange={(e) => setNovoValorCoins(e.target.value)} className="mt-1" />
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          className="flex-1"
                          disabled={!valido || !!actionLoading}
                          onClick={() => handleAction("propor_reajuste", {
                            modalidade: reajusteModalidade,
                            novo_valor: incluiDin ? valNum : undefined,
                            novo_valor_coins: incluiC ? coinsNum : undefined,
                          })}
                        >
                          {actionLoading === "propor_reajuste" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enviar Proposta"}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setShowReajuste(false)}>Cancelar</Button>
              </div>

              {reajuste && !(status === "aguardando_anunciante" && isAnunciante && !reajusteParaMimResponder) && (() => {
                const rMod = (reajuste.modalidade || "dinheiro") as string;
                const rIncluiDin = rMod === "dinheiro" || rMod === "ambos";
                const rIncluiCoins = rMod === "coins" || rMod === "ambos";
                const partes: string[] = [];
                if (rIncluiDin) partes.push(`R$ ${Number(reajuste.valor || 0).toFixed(2).replace(".", ",")}`);
                if (rIncluiCoins) partes.push(`${Number(reajuste.valor_coins || 0).toLocaleString("pt-BR")} RC`);
                const propostaTxt = partes.join(" + ");
                return (
                  <div className="mt-2 p-2 rounded-lg border border-amber-500/30 bg-amber-500/10 space-y-2">
                    <p className="text-xs text-amber-400 font-medium flex items-center gap-1">
                      <DollarSign className="w-3 h-3" />
                      Proposta de reajuste: {propostaTxt}
                    </p>
                    {reajustePropostoPorMim && (
                      <div className="flex items-center gap-2">
                        <p className="text-[10px] text-muted-foreground flex-1">Aguardando resposta da outra parte...</p>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-6 text-[10px]"
                          disabled={!!actionLoading}
                          onClick={() => handleAction("cancelar_reajuste")}
                        >
                          {actionLoading === "cancelar_reajuste" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Ban className="w-3 h-3 mr-1" />}
                          Cancelar proposta
                        </Button>
                      </div>
                    )}
                    {reajusteParaMimResponder && !showAceitarReajuste && (
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          className="h-7 text-[10px]"
                          disabled={!!actionLoading}
                          onClick={() => {
                            if (rIncluiCoins) {
                              setPersonagemReceptorCoins("");
                              setShowAceitarReajuste(true);
                            } else {
                              handleAction("responder_reajuste", { aceitar: true });
                            }
                          }}
                        >
                          {actionLoading === "responder_reajuste" ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3 mr-1" />}
                          Aceitar
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          className="h-7 text-[10px]"
                          disabled={!!actionLoading}
                          onClick={() => handleAction("responder_reajuste", { aceitar: false })}
                        >
                          <XCircle className="w-3 h-3 mr-1" /> Recusar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-[10px] bg-accent hover:bg-accent/60 text-accent-foreground"
                          disabled={!!actionLoading}
                          onClick={() => {
                            setReajusteModalidade(rMod as any);
                            setNovoValor(rIncluiDin ? Number(reajuste.valor || 0).toFixed(2) : "");
                            setNovoValorCoins(rIncluiCoins ? String(reajuste.valor_coins || "") : "");
                            setShowReajuste(true);
                            setTimeout(() => {
                              reajusteFormRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                              novoValorInputRef.current?.focus();
                            }, 80);
                          }}
                        >
                          <DollarSign className="w-3 h-3 mr-1" /> Contrapropor
                        </Button>
                      </div>
                    )}
                    {reajusteParaMimResponder && showAceitarReajuste && rIncluiCoins && (
                      <div className="space-y-2 p-2 rounded border border-amber-500/30 bg-background/40">
                        <Label className="text-[10px]">Personagem que receberá as coins *</Label>
                        <Input
                          value={personagemReceptorCoins}
                          onChange={(e) => setPersonagemReceptorCoins(e.target.value)}
                          placeholder="Nome exato do personagem"
                          maxLength={60}
                          className="h-8 text-xs"
                        />
                        <p className="text-[9px] text-muted-foreground">O intermediador enviará as coins para esse personagem após o pagamento ser concluído.</p>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            className="flex-1 h-7 text-[10px]"
                            disabled={!personagemReceptorCoins.trim() || !!actionLoading}
                            onClick={() => handleAction("responder_reajuste", {
                              aceitar: true,
                              personagem_receptor_coins: personagemReceptorCoins.trim(),
                            })}
                          >
                            {actionLoading === "responder_reajuste" ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3 mr-1" />}
                            Confirmar aceite
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={() => setShowAceitarReajuste(false)}>
                            Voltar
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
                    </div>
                  );
                })()}

                {/* Anunciante: Accept/Refuse/Propor */}
                {status === "aguardando_anunciante" && isAnunciante && !showRecusar && !showReajuste && !reajusteParaMimResponder && (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      className="flex-1 min-w-[120px]"
                      disabled={!!actionLoading}
                      onClick={() => {
                        if (!chavePix.trim()) {
                          toast({ title: "Informe sua chave PIX antes de aceitar", variant: "destructive" });
                          return;
                        }
                        if (!nomeTitularPix.trim()) {
                          toast({ title: "Informe o nome do titular da chave PIX", variant: "destructive" });
                          return;
                        }
                        const persTrim = personagemAnunciante.trim();
                        if (!persTrim) {
                          toast({ title: "Informe o nome do personagem envolvido na transação", variant: "destructive" });
                          personagemAnuncianteInputRef.current?.focus();
                          return;
                        }
                        if (!personagemAnuncianteValidated || personagemAnuncianteLastValidated !== persTrim) {
                          setPendingAceitar(true);
                          charValidatorRef.current?.validate(persTrim);
                          return;
                        }
                        handleAceitarAnunciante(persTrim);
                      }}
                    >
                      {actionLoading === "responder_anunciante" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />}
                      Aceitar
                    </Button>
                    <Button size="sm" variant="destructive" className="flex-1 min-w-[120px]" onClick={() => setShowRecusar(true)}>
                      <XCircle className="w-4 h-4 mr-1" /> Recusar
                    </Button>
                    {canProposeReajuste && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 min-w-[120px] bg-accent hover:bg-accent/60 text-accent-foreground"
                        onClick={() => {
                          setReajusteModalidade((inter.modalidade_pagamento || "dinheiro") as any);
                          setNovoValor(Number(inter.valor_negociado || 0).toFixed(2));
                          setNovoValorCoins(String(inter.valor_negociado_coins || ""));
                          setShowReajuste(true);
                        }}
                      >
                        <DollarSign className="w-4 h-4 mr-1" /> Propor Reajuste
                      </Button>
                    )}
                  </div>
                )}

                {status === "aguardando_anunciante" && isAnunciante && (
                  <div className="space-y-2">
                    <div>
                      <Label className="text-xs">Sua chave PIX para recebimento *</Label>
                      <Input
                        value={chavePix}
                        onChange={(e) => setChavePix(e.target.value)}
                        placeholder="CPF, e-mail, telefone ou chave aleatória"
                        className={`mt-1 ${!chavePix.trim() ? "border-destructive/50" : ""}`}
                      />
                      {!chavePix.trim() && (
                        <p className="text-[9px] text-destructive mt-1">Obrigatório para aceitar a proposta</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs">Nome do titular da chave PIX *</Label>
                      <Input
                        value={nomeTitularPix}
                        onChange={(e) => setNomeTitularPix(e.target.value)}
                        placeholder="Nome completo do titular"
                        maxLength={120}
                        className={`mt-1 ${!nomeTitularPix.trim() ? "border-destructive/50" : ""}`}
                      />
                      {!nomeTitularPix.trim() && (
                        <p className="text-[9px] text-destructive mt-1">Obrigatório para aceitar a proposta</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs">Personagem envolvido na transação *</Label>
                      <div className="relative mt-1">
                        <Input
                          ref={personagemAnuncianteInputRef}
                          value={personagemAnunciante}
                          onChange={(e) => {
                            setPersonagemAnunciante(e.target.value);
                            if (personagemAnuncianteValidated && e.target.value.trim() !== personagemAnuncianteLastValidated) {
                              setPersonagemAnuncianteValidated(false);
                            }
                          }}
                          placeholder="Nome exato do personagem no RubinOT"
                          maxLength={60}
                          className={!personagemAnunciante.trim() ? "border-destructive/50" : ""}
                        />
                        {personagemAnuncianteValidated && personagemAnunciante.trim() === personagemAnuncianteLastValidated && (
                          <CheckCircle2 className="w-4 h-4 text-green-500 absolute right-2 top-1/2 -translate-y-1/2" />
                        )}
                      </div>
                      {!personagemAnunciante.trim() ? (
                        <p className="text-[9px] text-destructive mt-1">Obrigatório para aceitar a proposta</p>
                      ) : (
                        <p className="text-[9px] text-muted-foreground mt-1">Será validado no RubinOT antes de prosseguir.</p>
                      )}
                    </div>
                  </div>
                )}

                {showRecusar && (
                  <div className="space-y-2 p-3 rounded-lg border border-destructive/30 bg-destructive/5">
                    <Label className="text-xs">Justificativa da recusa</Label>
                    <Textarea
                      value={justificativa}
                      onChange={(e) => setJustificativa(e.target.value)}
                      placeholder="Informe o motivo..."
                      rows={2}
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="destructive"
                        className="flex-1"
                        disabled={!justificativa.trim() || !!actionLoading}
                        onClick={() => handleAction("responder_anunciante", { aceitar: false, justificativa })}
                      >
                        Confirmar Recusa
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setShowRecusar(false)}>Cancelar</Button>
                    </div>
                  </div>
                )}

                {/* Admin: Enter chat */}
                {status === "aguardando_intermediador" && isAdmin && (
                  <Button
                    size="sm"
                    className="w-full"
                    disabled={!!actionLoading}
                    onClick={() => handleAction("admin_entrar")}
                  >
                    {actionLoading === "admin_entrar" ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4 mr-1" />}
                    Entrar como Intermediador
                  </Button>
                )}

                {/* Admin: Generate item payment (only for dinheiro or ambos) */}
                {status === "em_andamento" && isAdmin && (modalidade === "dinheiro" || modalidade === "ambos") && (
                  <Button
                    size="sm"
                    className="w-full"
                    disabled={!!actionLoading}
                    onClick={() => handleAction("gerar_pagamento_item")}
                  >
                    {actionLoading === "gerar_pagamento_item" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4 mr-1" />}
                    Gerar Pagamento do Item (R$)
                  </Button>
                )}

                {/* User: Confirm coin sending - only after fee is paid */}
                {(modalidade === "coins" || modalidade === "ambos") && !inter.coins_transferido && !inter.coins_comprovante_url && !isTerminal && !["aguardando_anunciante", "aguardando_pagamento_intermedio"].includes(status) && isCoinSender && !isAdmin && (
                  <Button
                    size="sm"
                    className="w-full"
                    disabled={!!actionLoading}
                    onClick={() => handleAction("confirmar_envio_coins")}
                  >
                    {actionLoading === "confirmar_envio_coins" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Coins className="w-4 h-4 mr-1" />}
                    Confirmar Envio de {Number(inter.valor_negociado_coins || 0).toLocaleString("pt-BR")} RC
                  </Button>
                )}

                {/* User: Coins already sent, awaiting admin */}
                {(modalidade === "coins" || modalidade === "ambos") && !inter.coins_transferido && inter.coins_comprovante_url && !isTerminal && !["aguardando_anunciante", "aguardando_pagamento_intermedio"].includes(status) && !isAdmin && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground p-2 rounded-lg bg-primary/5 border border-primary/20">
                    <Clock className="w-3 h-3" />
                    <span>Envio de coins confirmado. Aguardando administrador verificar.</span>
                  </div>
                )}

                {/* Admin: Confirm coins received - only after fee is paid and user indicates sending */}
                {isAdmin && (modalidade === "coins" || modalidade === "ambos") && !inter.coins_transferido && inter.coins_comprovante_url && !isTerminal && !["aguardando_anunciante", "aguardando_pagamento_intermedio"].includes(status) && (
                  <div className="space-y-1">
                    {inter.coins_comprovante_url && (
                      <a href={inter.coins_comprovante_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary underline flex items-center gap-1">
                        <Eye className="w-3 h-3" /> Ver comprovante de envio das coins
                      </a>
                    )}
                    <Button
                      size="sm"
                      className="w-full"
                      disabled={!!actionLoading}
                      onClick={() => handleAction("confirmar_coins")}
                    >
                      {actionLoading === "confirmar_coins" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Coins className="w-4 h-4 mr-1" />}
                      Confirmar Recebimento de Coins ({Number(inter.valor_negociado_coins || 0).toLocaleString("pt-BR")} RC)
                    </Button>
                  </div>
                )}

                {/* Interested: Item payment via PIX (in-app dialog) */}
                {status === "aguardando_pagamento_item" && isInteressado && (inter.item_pix_qr_code_base64 || inter.item_pix_qr_code || inter.abacatepay_item_url) && (
                  <Button
                    size="sm"
                    className="w-full"
                    onClick={() => {
                      const itemKey = `item-${inter.id}`;
                      qrDismissedRef.current.delete(itemKey);
                      currentQrPaymentIdRef.current = itemKey;
                      if (inter.item_pix_qr_code_base64 || inter.item_pix_qr_code) {
                        setQrPayment({
                          valor: Number(inter.valor_negociado),
                          qrCodeBase64: inter.item_pix_qr_code_base64 || undefined,
                          qrCode: inter.item_pix_qr_code || undefined,
                          url: inter.abacatepay_item_url || undefined,
                        });
                      } else if (inter.abacatepay_item_url) {
                        window.open(inter.abacatepay_item_url, "_blank");
                      }
                    }}
                  >
                    <CreditCard className="w-4 h-4 mr-1" /> Pagar R$ {Number(inter.valor_negociado).toFixed(2).replace(".", ",")}
                  </Button>
                )}

                {/* Admin: Confirm item payment received */}
                {status === "aguardando_conferencia_admin" && isAdmin && (
                  <Button
                    size="sm"
                    className="w-full"
                    disabled={!!actionLoading}
                    onClick={() => handleAction("confirmar_pagamento_item")}
                  >
                    {actionLoading === "confirmar_pagamento_item" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />}
                    Confirmar Pagamento Recebido
                  </Button>
                )}

                {/* Item sender: Confirm item sent */}
                {status === "aguardando_envio_item" && isItemSender && !isAdmin && (
                  <Button
                    size="sm"
                    className="w-full"
                    disabled={!!actionLoading}
                    onClick={() => { setEnvioVideoError(null); setShowEnvioVideoDialog(true); }}
                  >
                    {actionLoading === "verificar_video_envio_item" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Package className="w-4 h-4 mr-1" />}
                    Confirmar Envio do Item
                  </Button>
                )}

                {/* Item receiver: Confirm receipt */}
                {status === "aguardando_confirmacao_recebimento" && isItemReceiver && (
                  <Button
                    size="sm"
                    className="w-full"
                    disabled={!!actionLoading}
                    onClick={() => handleAction("confirmar_recebimento_item")}
                  >
                    {actionLoading === "confirmar_recebimento_item" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />}
                    Confirmar Recebimento do Item
                  </Button>
                )}

                {/* Admin: Reject submitted video and ask sender to resend */}
                {status === "aguardando_confirmacao_recebimento" && isAdmin && (
                  <Button
                    size="sm"
                    variant="destructive"
                    className="w-full"
                    disabled={!!actionLoading}
                    onClick={async () => {
                      const motivo = window.prompt(
                        "Informe o motivo da recusa do vídeo (opcional). O remetente será notificado e deverá enviar um novo vídeo.",
                        "",
                      );
                      if (motivo === null) return; // cancelou
                      setActionLoading("admin_recusar_video");
                      try {
                        const { data: res } = await supabase.functions.invoke("intermediacao", {
                          body: {
                            action: "admin_recusar_video",
                            contato: sessionContato,
                            token: sessionToken,
                            intermediacao_id: intermediacaoId,
                            motivo,
                          },
                        });
                        if (res?.success) {
                          toast({ title: "Vídeo recusado", description: "O remetente foi notificado para enviar um novo vídeo." });
                          loadDetails();
                          onStatusChange?.();
                        } else {
                          toast({ title: "Erro", description: res?.error || "Não foi possível recusar o vídeo", variant: "destructive" });
                        }
                      } catch (e: any) {
                        toast({ title: "Erro", description: e?.message || "Falha ao recusar vídeo", variant: "destructive" });
                      } finally {
                        setActionLoading(null);
                      }
                    }}
                  >
                    {actionLoading === "admin_recusar_video" ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4 mr-1" />}
                    Recusar Vídeo
                  </Button>
                )}

                {/* Admin: Upload comprovante + confirm payment to anunciante */}
                {status === "aguardando_pagamento_anunciante" && isAdmin && (
                  <div className="space-y-2">
                    {/* Botão "Anexar Comprovante" removido a pedido — o admin não precisa subir comprovante para concluir o pagamento ao anunciante */}
                    <Button
                      size="sm"
                      className="w-full"
                      disabled={!!actionLoading}
                      onClick={() => handleAction("confirmar_pagamento_anunciante_admin")}
                    >
                      Confirmar Pagamento ao Anunciante
                    </Button>
                  </div>
                )}

                {/* Anunciante: Confirm payment received */}
                {status === "aguardando_confirmacao_pagamento" && isAnunciante && !isAdmin && (
                  <Button
                    size="sm"
                    className="w-full"
                    disabled={!!actionLoading}
                    onClick={() => handleAction("confirmar_pagamento_anunciante")}
                  >
                    {actionLoading === "confirmar_pagamento_anunciante" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />}
                    Confirmar Recebimento do Pagamento
                  </Button>
                )}

                {/* Comprovante display */}
                {inter.comprovante_pagamento_url && (
                  <a href={inter.comprovante_pagamento_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary underline flex items-center gap-1">
                    <Eye className="w-3 h-3" /> Ver comprovante de pagamento
                  </a>
                )}

                {/* Price renegotiation button (other contexts: interessado, etc.) */}
                {canProposeReajuste && !showReajuste && !(status === "aguardando_anunciante" && isAnunciante) && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full bg-accent hover:bg-accent/60 text-accent-foreground"
                    onClick={() => {
                      setReajusteModalidade((inter.modalidade_pagamento || "dinheiro") as any);
                      setNovoValor(Number(inter.valor_negociado || 0).toFixed(2));
                      setNovoValorCoins(String(inter.valor_negociado_coins || ""));
                      setShowReajuste(true);
                    }}
                  >
                    <DollarSign className="w-4 h-4 mr-1" /> Propor Reajuste de Preço
                  </Button>
                )}

                {showReajuste && !(status === "aguardando_anunciante" && isAnunciante) && (() => {
                  const incluiDin = reajusteModalidade === "dinheiro" || reajusteModalidade === "ambos";
                  const incluiC = reajusteModalidade === "coins" || reajusteModalidade === "ambos";
                  const valNum = parseFloat(novoValor || "0");
                  const coinsNum = parseInt(novoValorCoins || "0", 10);
                  const valido =
                    (!incluiDin || (valNum > 0)) &&
                    (!incluiC || (coinsNum > 0));
                  return (
                    <div ref={reajusteFormRef} className="space-y-2 p-3 rounded-lg border border-amber-500/30 bg-amber-500/10">
                      <Label className="text-xs">Modalidade da proposta</Label>
                      <div className="flex gap-2 flex-wrap">
                        {(["dinheiro", "coins", "ambos"] as const).map((m) => (
                          <Button
                            key={m}
                            size="sm"
                            variant={reajusteModalidade === m ? "default" : "outline"}
                            className="h-7 text-[10px]"
                            onClick={() => setReajusteModalidade(m)}
                          >
                            {m === "dinheiro" ? "Dinheiro" : m === "coins" ? "Coins" : "Dinheiro + Coins"}
                          </Button>
                        ))}
                      </div>
                      {incluiDin && (
                        <div>
                          <Label className="text-xs">Valor em dinheiro (R$)</Label>
                          <Input ref={novoValorInputRef} type="number" step="0.01" min="0.01" value={novoValor} onChange={(e) => setNovoValor(e.target.value)} className="mt-1" />
                        </div>
                      )}
                      {incluiC && (
                        <div>
                          <Label className="text-xs">Valor em coins (RC)</Label>
                          <Input ref={!incluiDin ? novoValorInputRef : undefined} type="number" step="1" min="1" value={novoValorCoins} onChange={(e) => setNovoValorCoins(e.target.value)} className="mt-1" />
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          className="flex-1"
                          disabled={!valido || !!actionLoading}
                          onClick={() => handleAction("propor_reajuste", {
                            modalidade: reajusteModalidade,
                            novo_valor: incluiDin ? valNum : undefined,
                            novo_valor_coins: incluiC ? coinsNum : undefined,
                          })}
                        >
                          {actionLoading === "propor_reajuste" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enviar Proposta"}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setShowReajuste(false)}>Cancelar</Button>
                      </div>
                    </div>
                  );
                })()}

                {/* Avaliação prompt for interessado when concluded */}
                {status === "concluido" && isInteressado && !showAvaliacao && (
                  <Button
                    size="sm"
                    className="w-full"
                    onClick={() => setShowAvaliacao(true)}
                  >
                    <Star className="w-4 h-4 mr-1" />
                    Avaliar Anunciante
                  </Button>
                )}
              </div>

              {/* Inline AvaliarForm */}
              {status === "concluido" && isInteressado && showAvaliacao && (
                <div className="border border-primary/30 rounded-lg p-3 bg-primary/5 space-y-2">
                  <p className="font-pixel text-[9px] text-primary">Avalie sua experiência com {inter.anunciante?.apelido || inter.anunciante?.nome || "o anunciante"}</p>
                  <AvaliarForm
                    avaliadoId={inter.anunciante_id}
                    avaliadoApelido={inter.anunciante?.apelido || inter.anunciante?.nome || "Anunciante"}
                    onSuccess={() => setShowAvaliacao(false)}
                    onRequireLogin={() => {}}
                    defaultItems={inter.anuncios?.item_nome ? [inter.anuncios.item_nome] : undefined}
                    defaultDate={new Date().toISOString().slice(0, 10)}
                  />
                </div>
              )}

              {/* Message input */}
              {!["recusado", "cancelado", "concluido"].includes(status) && (
                <div className="flex gap-2 pb-1">
                  <Input
                    ref={msgInputRef}
                    value={msgInput}
                    onChange={(e) => setMsgInput(e.target.value)}
                    placeholder="Digite uma mensagem..."
                    onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendMessage()}
                    disabled={sending}
                  />
                  <Button size="sm" onClick={sendMessage} disabled={!msgInput.trim() || sending}>
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>

    {/* Time expired dialog */}
    <AlertDialog open={showTimeExpiredDialog} onOpenChange={setShowTimeExpiredDialog}>
      <AlertDialogContent className="tibia-panel border-destructive/50">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-pixel text-sm text-destructive flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Tempo Esgotado
          </AlertDialogTitle>
          <AlertDialogDescription className="text-sm">
            O processo de intermediação foi cancelado automaticamente por inatividade (15 minutos sem atividade no chat).
            O anúncio voltará a ser exibido normalmente na lista.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction onClick={() => { setShowTimeExpiredDialog(false); onOpenChange(false); }}>
            Entendi
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Payment confirmation dialog */}
    <Dialog open={!!paymentConfirmedDialog} onOpenChange={(v) => { if (!v) setPaymentConfirmedDialog(null); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-pixel text-sm flex items-center gap-2">
            <span>✅</span> Pagamento confirmado
          </DialogTitle>
        </DialogHeader>
        {paymentConfirmedDialog && (
          <div className="flex flex-col items-center gap-3 py-2 text-center">
            <p className="text-sm">
              {paymentConfirmedDialog.tipo === "item" ? "Pagamento do item" : "Pagamento da taxa de intermédio"} realizado por{" "}
              <span className="font-bold text-primary">{paymentConfirmedDialog.payerName}</span>.
            </p>
            <p className="text-lg font-bold text-primary">
              R$ {paymentConfirmedDialog.valor.toFixed(2).replace(".", ",")}
            </p>
            <Button onClick={() => setPaymentConfirmedDialog(null)} className="mt-2">OK</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>

    {/* QR Code Payment Dialog */}
    <Dialog open={!!qrPayment} onOpenChange={(val) => {
      if (!val) {
        if (currentQrPaymentIdRef.current) {
          qrDismissedRef.current.add(currentQrPaymentIdRef.current);
          currentQrPaymentIdRef.current = null;
        }
        setQrPayment(null);
      }
    }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-pixel text-sm flex items-center gap-2">
            <QrCode className="w-5 h-5 text-primary" />
            Pagamento PIX
          </DialogTitle>
        </DialogHeader>
        {qrPayment && (
          <div className="flex flex-col items-center gap-4 py-2">
            {/* Item info */}
            <div className="flex flex-col items-center gap-2">
              {resolvedItemImageUrl && (
                <img
                  src={resolvedItemImageUrl}
                  alt={inter?.anuncios?.item_nome || "Item"}
                  className="w-12 h-12 object-contain"
                />
              )}
              <p className="font-pixel text-[10px] text-primary text-center">
                {inter?.anuncios?.item_nome || "Item"}
              </p>
            </div>
            <p className="text-lg font-bold text-primary">
              R$ {Number(qrPayment.valor).toFixed(2).replace(".", ",")}
            </p>

            {qrPayment.qrCodeBase64 && (
              <div className="relative">
                <img
                  src={`data:image/png;base64,${qrPayment.qrCodeBase64}`}
                  alt="QR Code PIX"
                  className="w-52 h-52 rounded-md border border-border"
                />
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <img src={logoImg} alt="RPG Coins" className="w-10 h-10 rounded-full bg-background border-2 border-primary shadow-md object-cover" />
                </div>
              </div>
            )}

            {qrPayment.qrCode && (
              <div className="w-full space-y-1">
                <p className="text-[10px] text-muted-foreground text-center">Ou copie o código PIX:</p>
                <div className="flex gap-1">
                  <input
                    type="text"
                    readOnly
                    value={qrPayment.qrCode}
                    className="flex-1 text-[9px] px-2 py-1.5 rounded-md border border-border bg-secondary/50 truncate"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-2"
                    onClick={() => {
                      navigator.clipboard.writeText(qrPayment.qrCode!);
                      toast({ title: "Código PIX copiado!" });
                    }}
                  >
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            )}

            {/* Waiting animation */}
            <div className="flex flex-col items-center gap-2 pt-2">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin text-primary" />
                <span>Aguardando pagamento...</span>
              </div>
              <div className="flex gap-1">
                <span className="w-2 h-2 rounded-full bg-primary animate-bounce [animation-delay:0ms]" />
                <span className="w-2 h-2 rounded-full bg-primary animate-bounce [animation-delay:150ms]" />
                <span className="w-2 h-2 rounded-full bg-primary animate-bounce [animation-delay:300ms]" />
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
    <RubinotCharacterValidator
      ref={charValidatorRef}
      requireValidation={configData?.somente_personagem_validado !== "false"}
      onConfirm={(info) => {
        setPersonagemAnunciante(info.name);
        setPersonagemAnuncianteLastValidated(info.name);
        setPersonagemAnuncianteValidated(true);
        if (pendingAceitar) {
          setPendingAceitar(false);
          handleAceitarAnunciante(info.name);
        }
      }}
      onProceedUnvalidated={(name) => {
        setPersonagemAnuncianteLastValidated(name);
        setPersonagemAnuncianteValidated(true);
        if (pendingAceitar) {
          setPendingAceitar(false);
          handleAceitarAnunciante(name);
        }
      }}
      onCorrect={() => {
        setPendingAceitar(false);
        setPersonagemAnuncianteValidated(false);
        setTimeout(() => personagemAnuncianteInputRef.current?.focus(), 100);
      }}
    />
    <Dialog open={showEnvioVideoDialog} onOpenChange={() => { /* dialog fecha apenas quando o vídeo for recebido */ }}>
      <DialogContent className="max-w-md" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Package className="w-4 h-4" /> Validação do envio do item</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          {(() => {
            const lastSys = [...mensagens].reverse().find((m: any) => m.tipo === "sistema");
            const recusado = lastSys && typeof lastSys.conteudo === "string" && lastSys.conteudo.includes("Vídeo recusado pelo administrador");
            if (!recusado) return null;
            return (
              <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-3">
                <p className="font-semibold text-destructive mb-1 flex items-center gap-2">
                  <XCircle className="w-4 h-4" /> Vídeo recusado pelo administrador
                </p>
                <p className="text-xs text-foreground/90 whitespace-pre-wrap">{lastSys.conteudo}</p>
                <p className="text-xs text-muted-foreground mt-2">Grave e envie um novo vídeo comprovando a transferência do item.</p>
              </div>
            );
          })()}
          <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
            <p className="font-semibold text-foreground mb-1">Como confirmar o envio:</p>
            <ol className="text-muted-foreground leading-relaxed list-decimal pl-4 space-y-1">
              <li>Grave um vídeo efetuando a transferência do item em seu celular.</li>
              <li>Envie o vídeo para o WhatsApp do RPG Coins.</li>
              <li>Aguarde — assim que recebermos o vídeo, esta janela fecha automaticamente e o vídeo aparece no chat. O recebimento do vídeo confirma o envio do item.</li>
            </ol>
          </div>
          <div className="flex items-center gap-2 rounded-md border border-primary/20 bg-secondary/30 px-3 py-2">
            <Loader2 className="w-4 h-4 animate-spin text-primary" />
            <span className="text-xs text-muted-foreground">Aguardando recebimento do vídeo no WhatsApp...</span>
          </div>
          {envioVideoError && (
            <div className="rounded-md bg-destructive/10 text-destructive text-xs p-2 border border-destructive/30">
              {envioVideoError}
            </div>
          )}
          <div className="flex flex-col gap-2">
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              disabled={actionLoading === "verificar_video_envio_item"}
              onClick={async () => {
                setEnvioVideoError(null);
                setActionLoading("verificar_video_envio_item");
                try {
                  const { data: res } = await supabase.functions.invoke("intermediacao", {
                    body: {
                      action: "verificar_video_envio_item",
                      contato: sessionContato,
                      token: sessionToken,
                      intermediacao_id: intermediacaoId,
                    },
                  });
                  if (res?.success && res?.found) {
                    toast({ title: "Envio confirmado com vídeo!" });
                    setShowEnvioVideoDialog(false);
                    loadDetails();
                    onStatusChange?.();
                  } else {
                    setEnvioVideoError(res?.error || "Nenhum vídeo recebido ainda.");
                  }
                } catch {
                  setEnvioVideoError("Erro ao verificar. Tente novamente.");
                } finally {
                  setActionLoading(null);
                }
              }}
            >
              {actionLoading === "verificar_video_envio_item" ? (
                <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Verificando...</>
              ) : (
                <><CheckCircle className="w-4 h-4 mr-1" /> Verificar agora</>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  </>);
};

export default IntermedioChatDialog;
